import { lazy, Suspense, useState, useEffect } from "react";
import { useTextScramble } from "@/hooks/useTextScramble";

const LiquidGradient = lazy(() => import("@/components/LiquidGradient"));

const HeroSection = () => {
  const [showLine1, setShowLine1] = useState(false);
  const [showScramble, setShowScramble] = useState(false);

  const prefersReducedMotion =
    typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const { display, resolved } = useTextScramble("AI Builder.", {
    delay: prefersReducedMotion ? 0 : 450,
    duration: 800,
  });

  useEffect(() => {
    if (prefersReducedMotion) {
      setShowLine1(true);
      setShowScramble(true);
      return;
    }
    const t1 = setTimeout(() => setShowLine1(true), 100);
    const t2 = setTimeout(() => setShowScramble(true), 300);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [prefersReducedMotion]);

  const handleCTA = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      <Suspense fallback={<div className="absolute inset-0 bg-background" />}>
        <LiquidGradient />
      </Suspense>
      <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-background to-transparent pointer-events-none z-[1]" />

      <div className="content-max relative z-10 pt-20">
        <div className="max-w-4xl">
          <h1 className="font-headline text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-foreground leading-[1.05] tracking-tight mb-8">
            <span
              className="block transition-all duration-700 ease-out"
              style={{
                opacity: showLine1 ? 1 : 0,
                transform: showLine1 ? "translateY(0)" : "translateY(12px)",
              }}
            >
              Growth Operator.
            </span>
            <span
              className="block transition-all duration-500 ease-out"
              style={{
                opacity: showScramble ? 1 : 0,
                transform: showScramble ? "translateY(0)" : "translateY(12px)",
              }}
            >
              <span className={resolved ? "italic" : "font-mono"}>
                {showScramble ? display : ""}
              </span>
            </span>
          </h1>

          <p className="font-body text-base sm:text-lg md:text-xl text-muted-foreground leading-relaxed max-w-2xl mb-10">
            I help startups build predictable acquisition, retention, and revenue
            systems — and I build the AI-powered tools to accelerate them.
            <br />
            
            <span className="font-mono text-sm md:text-base text-foreground/70">
              10+ years scaling consumer tech brands including Crunchyroll (45M
              users, $1.2B acquisition) and Castle (2.2M MAU, $15M seed round).
            </span>
          </p>

          <a
            href="#contact"
            onClick={handleCTA}
            className="inline-block font-body text-sm font-medium bg-primary text-primary-foreground px-8 py-3.5 rounded-md hover:opacity-90 transition-opacity"
          >
            Let's Talk
          </a>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
